import React from 'react';
import { useCreateBrandMutation } from '@/api/queries';
import type { CreateOutcome } from '@/utils';
import type { BrandFormSubmit } from './components/brandFormModal/useBrandForm';

/**
 * Creating a brand — the one definition, shared by both places that do it.
 *
 * The brands page's toolbar opens the form, and so does the Control Center's
 * "Add brand". Before this hook the create branch lived in `useBrandsPage`,
 * which meant the launcher could either import a page hook wholesale — pulling
 * a list query, pagination and a delete dialog along with it — or write the
 * POST a second time and let the two drift.
 *
 * Only the **create** path is here. Editing is still the page's, because only
 * the page edits, and because the edit path is genuinely different: it uploads
 * a fresh image separately before the write, where a create sends one
 * multipart round-trip.
 *
 * Cache invalidation is not this hook's business — `useCreateBrandMutation`
 * already invalidates `['brands','list']` in its own `onSuccess`, which is what
 * makes the new row visible when the launcher's confirmation link is followed.
 */
export const useCreateBrand = () => {
    const createMutation = useCreateBrandMutation();

    const create = React.useCallback(
        async (input: BrandFormSubmit): Promise<CreateOutcome> => {
            const result = await createMutation.mutateAsync({
                name: input.name,
                imageUpload: input.imageUpload,
            });

            return result.success
                ? { ok: true }
                : {
                      ok: false,
                      message:
                          result.error.message ?? 'Failed to create brand.',
                  };
        },
        [createMutation],
    );

    return { create, isCreating: createMutation.isPending };
};
